import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeePortalAccessReleaseModal } from "@/pages/employees/employee/widgets/EmployeePortalAccessReleaseModal.tsx";
import { EmployeePortalAccessWorkflowsModal } from "@/pages/employees/employee/widgets/EmployeePortalAccessWorkflowsModal.tsx";
import { useWorkflowExecutions } from "@/pages/workflows/workflow-execution-hook.tsx";
import { useEmployeePortalAccess } from "@/queries/preboarding/use-preboarding.query.ts";
import {
  ApiEmployee,
  getPortalAccessModeTitle,
  PortalAccessMode,
} from "@/types/employees/employee.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  Avatar,
  Button,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface Props extends ModalProps {
  employee: ApiEmployee;
}

export function EmployeePortalAccessModal({ ...props }: Props) {
  const { employee } = props;
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { canViewField, canEditField } = usePermissions();
  const { renderDate, renderDateAndTime, renderEmployeeName } = useFormatter();
  const { getWorkflowExecutionStatusTag } = useWorkflowExecutions();

  const canView = canViewField(employee, EmployeeFieldPermission.employee_portal_access);
  const canEdit = canEditField(employee, EmployeeFieldPermission.employee_portal_access);
  const { data: portalAccess, isLoading } = useEmployeePortalAccess(employee.id, canView);

  const isRestricted = portalAccess && portalAccess.mode !== PortalAccessMode.full;
  const isReleased = !!portalAccess?.datetime_released_at;

  return (
    <Modal
      {...props}
      title={t("core:portal_access")}
      variant={"middle"}
      layout={"base"}
      size={"md"}
      withCloser={true}
      headerActionsSlot={
        canEdit && isRestricted && !isReleased ? (
          <DropdownMenu
            trigger={
              <Button
                variant={"secondary"}
                rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
              >
                {t("common:actions")}
              </Button>
            }
          >
            <DropdownMenuItem
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
              title={t("core:portal_access_add_workflows")}
              onClick={() =>
                openModal(EmployeePortalAccessWorkflowsModal, {
                  employeeId: employee.id,
                  mode: "add",
                })
              }
            />
            {portalAccess.blocking_executions.some((execution) => execution.is_blocking) && (
              <DropdownMenuItem
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-arrows-rotate"} />}
                title={t("core:portal_access_replace_workflows")}
                onClick={() =>
                  openModal(EmployeePortalAccessWorkflowsModal, {
                    employeeId: employee.id,
                    mode: "replace",
                  })
                }
              />
            )}
            <DropdownMenuItem
              leftIcon={<FontAwesomeIcon icon={"fa-light fa-door-open"} />}
              title={t("core:portal_access_release")}
              onClick={() =>
                openModal(EmployeePortalAccessReleaseModal, { employeeId: employee.id })
              }
            />
          </DropdownMenu>
        ) : undefined
      }
    >
      <Stack gap={"xl"}>
        {isLoading && (
          <Stack gap={"md"}>
            <Skeleton className={"h-12 w-full"} />
            <Skeleton className={"h-12 w-full"} />
          </Stack>
        )}

        {portalAccess && (
          <>
            <Stack>
              <ListItem
                titleSlot={
                  <Text
                    className={twMerge(
                      "font-semibold",
                      isRestricted ? "text-warning" : "text-positive"
                    )}
                  >
                    {getPortalAccessModeTitle(portalAccess.mode)}
                  </Text>
                }
                caption={isRestricted ? t("core:portal_access_mode_hint") : undefined}
              />

              {portalAccess.date_hired_on && (
                <ListItem
                  preTitle={t("core:hired_on")}
                  title={renderDate(portalAccess.date_hired_on)}
                />
              )}

              {portalAccess.datetime_released_at && (
                <ListItem
                  preTitle={t("core:portal_access_released")}
                  titleSlot={
                    <DotSeparator>
                      {[
                        portalAccess.released_by_employee
                          ? renderEmployeeName(portalAccess.released_by_employee)
                          : null,
                        renderDateAndTime(portalAccess.datetime_released_at),
                      ].filter(Boolean)}
                    </DotSeparator>
                  }
                  caption={portalAccess.release_reason ?? undefined}
                />
              )}
            </Stack>

            <Stack gap={"md"}>
              <Title header={t("core:portal_access_blocking_processes")} size={"xs"} />

              {portalAccess.blocking_executions.length === 0 ? (
                <EmptyStateBase
                  variant={"primary"}
                  emoji={":unlock:"}
                  title={t("core:portal_access_no_blockers")}
                  description={
                    [
                      portalAccess.mode === PortalAccessMode.preboarding
                        ? t("core:portal_access_no_blockers_preboarding")
                        : undefined,
                      isReleased ? t("core:portal_access_released_hint") : undefined,
                    ]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                />
              ) : (
                <Stack className={"bg-dark/5 rounded-xs px-4"}>
                  {portalAccess.blocking_executions.map((execution) => (
                    <ListItem
                      key={execution.gate_id}
                      className={"last:border-b-0"}
                      forceShowAvatar={true}
                      to={`/workflows/executions/${execution.workflow_execution_id}`}
                      blank={true}
                      icon={
                        <Avatar icon={<FontAwesomeIcon icon={"fa-light fa-diagram-project"} />} />
                      }
                      title={execution.workflow_title}
                      caption={
                        execution.is_blocking
                          ? t("core:portal_access_blocking_now")
                          : t("core:portal_access_not_blocking")
                      }
                      truncate={true}
                      valueSlot={getWorkflowExecutionStatusTag(execution.workflow_execution_status)}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Modal>
  );
}
